import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, FormDialog, Grid, InputField } from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { UpdateClientPayload, actions as clientsActions, selectors as clientsSelectors } from 'store/clients';
import {
  doesFormHaveEmptyValue,
  doesFormHaveInitialValues,
  doesFormHaveError,
  onInputBlur,
  onValueChange,
  getUpdatedResourceWithFormValues
} from 'utils/forms';
import { useStyles } from '../styled';
import { CustomerForm, EditCustomerOverlayProps } from '../types';
import { customerSchema, getCustomerForm } from '../utils';

const EditCustomerOverlay = (props: EditCustomerOverlayProps) => {
  const { client, onClose } = props;

  const { updateClient } = useActions({
    updateClient: clientsActions.updateClient
  });

  const pending = useSelector(clientsSelectors.selectPending);

  const initialForm = useMemo(() => getCustomerForm(client), [client]);
  const [form, setForm] = useState<CustomerForm>(initialForm);

  const isStateFair = useMemo(() => client.name.trim() === 'State Fair of Texas', [client.name]);
  const hasEmptyValue = useMemo(() => doesFormHaveEmptyValue(form), [form]);
  const hasError = useMemo(() => doesFormHaveError(form), [form]);
  const hasInitialValues = useMemo(() => doesFormHaveInitialValues(form), [form]);
  const isButtonDisabled = useMemo(() => {
    return hasEmptyValue || hasError || hasInitialValues;
  }, [hasEmptyValue, hasError, hasInitialValues]);

  const classes = useStyles();

  const onSaveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const updatedClient = getUpdatedResourceWithFormValues(client, form);
    // Specify which keys to update to avoid immutable keys like createdAt and updatedAt
    const payload: UpdateClientPayload = {
      id: updatedClient.id,
      configuration: updatedClient.configuration,
      name: updatedClient.name
    };
    updateClient(payload);
  };

  return (
    <FormDialog
      cancelButtonAction={onClose}
      cancelButtonText="Cancel"
      confirmButtonAction={onSaveClick}
      confirmButtonDisabled={isButtonDisabled}
      confirmButtonIsLoading={pending}
      confirmButtonText="Save"
      open
      title="Edit Customer"
    >
      <Grid className={classes.overlayInnerContainer}>
        {/* INPUT - NAME */}
        <Box className={classes.overlayFieldBox}>
          <InputField
            data-testid="name-input-field"
            error={form.name.error !== null}
            fullWidth
            helperText={form.name.error}
            label="Name"
            onBlur={(e) => onInputBlur(e, 'name', customerSchema, setForm)}
            onChange={(e) => onValueChange(e.target.value, 'name', setForm)}
            size="small"
            value={form.name.value}
          />
        </Box>

        {/* INPUT - RESTRICTED FONTS */}
        <Box className={classes.overlayLargeFieldBox}>
          <InputField
            data-testid="restricted-fonts-input-field"
            error={form.restrictedFonts.error !== null}
            fullWidth
            helperText={form.restrictedFonts.error}
            label="Restricted Fonts"
            size="small"
            multiline
            onBlur={(e) => onInputBlur(e, 'restrictedFonts', customerSchema, setForm)}
            onChange={(e) => onValueChange(e.target.value, 'restrictedFonts', setForm)}
            rows={10}
            sx={{ height: '270px' }}
            value={form.restrictedFonts.value}
          />
        </Box>

        {/* INPUT - SALES REPORT SETTINGS (SFT ONLY) */}
        {isStateFair && (
          <Box className={classes.overlayLargeFieldBox}>
            <InputField
              data-testid="sales-report-settings-input-field"
              error={form.salesReportSettings.error !== null}
              fullWidth
              helperText={form.salesReportSettings.error}
              label="Sales Report Settings"
              size="small"
              multiline
              onBlur={(e) => onInputBlur(e, 'salesReportSettings', customerSchema, setForm)}
              onChange={(e) => onValueChange(e.target.value, 'salesReportSettings', setForm)}
              rows={10}
              sx={{ height: '270px' }}
              value={form.salesReportSettings.value}
            />
          </Box>
        )}
      </Grid>
    </FormDialog>
  );
};

export default EditCustomerOverlay;
